import { useNavigate } from "react-router-dom";

// Luxurious empty state for when there are no outfits yet
// Uses brand system: ultra-thin typography, gradients, subtle animations
export default function OutfitsEmptyState() {
  const navigate = useNavigate();

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-800/40 bg-gradient-to-br from-[#0b1220] via-[#0f1b2e] to-[#101826] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
      {/* Decorative gradient beams */}
      <div className="pointer-events-none absolute -top-40 -left-20 h-96 w-96 rounded-full bg-gradient-to-tr from-pink-500/10 via-fuchsia-400/10 to-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-gradient-to-tr from-indigo-400/10 via-purple-400/10 to-pink-400/10 blur-3xl" />

      <div className="relative px-6 py-12 sm:px-10 sm:py-16 lg:px-16 lg:py-20">
        {/* Icon + brand mark */}
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
          {/* Hanger Monogram */}
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-gray-300">
            <path
              fill="currentColor"
              d="M12 7a2 2 0 1 1 2-2 .75.75 0 0 0 1.5 0 3.5 3.5 0 1 0-5.31 3.04l.81.44c.67.36 1 .73 1 1.27 0 .6-.43 1.06-1.24 1.55L3 16.5A2.75 2.75 0 0 0 4.38 21h15.24A2.75 2.75 0 0 0 21 16.5l-8.76-5.07c.49-.35.76-.86.76-1.45 0-1.03-.64-1.8-1.66-2.35zM4.38 19.5a1.25 1.25 0 0 1-.63-2.33L12 12.4l8.25 4.77a1.25 1.25 0 0 1-.63 2.33z"
            />
          </svg>
        </div>

        {/* Copy */}
        <div className="mx-auto mt-6 max-w-2xl text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-gray-400">Atelier</p>
          <h1 className="mt-3 text-3xl font-extralight tracking-[0.25em] text-gray-100 sm:text-4xl">
            Compose Your First Ensemble
          </h1>
          <p className="mt-4 text-sm font-light leading-7 text-gray-300/90">
            Elevate neglected pieces into runway-ready statements. Add items to your wardrobe, then let our couture AI style a signature look tailored to your aesthetic.
          </p>
          {/* Keep legacy text for tests and accessibility */}
          <p className="sr-only">No outfits yet.</p>
        </div>

        {/* Actions */}
        <div className="mx-auto mt-10 flex max-w-lg flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={() => navigate("/wardrobe/new")}
            className="group relative inline-flex items-center justify-center rounded-md border border-white/15 bg-gradient-to-r from-pink-600 to-indigo-600 px-6 py-2 text-sm font-light uppercase tracking-wider text-white shadow-lg shadow-pink-600/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-indigo-600/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"
          >
            <span className="absolute inset-0 -z-10 rounded-md bg-white/10 opacity-0 blur transition-opacity duration-300 group-hover:opacity-100" />
            Create from Wardrobe
          </button>
          <button
            onClick={() => navigate("/wardrobe")}
            className="inline-flex items-center justify-center rounded-md border border-white/15 bg-white/5 px-6 py-2 text-sm font-light uppercase tracking-wider text-gray-200 backdrop-blur-sm transition-all duration-300 hover:bg-white/10"
          >
            Explore Wardrobe
          </button>
        </div>

        {/* Tips */}
        <div className="mx-auto mt-8 max-w-2xl text-center text-xs text-gray-400">
          Style note: Start with a hero piece. Our atelier harmonizes color, silhouette, and texture for a refined composition.
        </div>
      </div>
    </div>
  );
}
